/**
 * The one place V2's wizard vocabulary is translated into CMS's.
 *
 * Every other ported file speaks V2's strings verbatim so the UI stays faithful. The
 * cost of that fidelity is paid here, once, at the submit boundary: the wizard's
 * free-text option values are mapped onto the closed enums CMS binds, and anything CMS
 * has no member for is dropped rather than sent as a value CMS would reject.
 */
import type {
  CaseContact,
  CaseTarget,
  DataCategory,
  DomesticCaseForm,
  IdentifierType,
  RequestSubType,
  RequestType,
} from '../api/contracts';
import type { DomesticWizardState } from './domesticWizardSlice';

/** V2's USA request-type labels (requestTypes.ts) to CMS RequestType. */
const REQUEST_TYPE_MAP: Record<string, RequestType> = {
  'Subpoena or Summons': 'subpoenaSummons',
  'Court Order': 'courtOrder',
  'Search Warrant': 'searchWarrant',
  'PR/TT': 'prtt',
  'Lawful Intercept': 'lawfulIntercept',
  'Emergency Letter': 'emergencyLetter',
  Other: 'other',
};

/** V2's sub-type labels to CMS RequestSubType. */
const SUB_TYPE_MAP: Record<string, RequestSubType> = {
  Start: 'start',
  Renewal: 'renew',
  Stop: 'cease',
};

/** V2 identifier-type values (identifierTypes.ts) to CMS IdentifierType. */
const IDENTIFIER_TYPE_MAP: Record<string, IdentifierType> = {
  CID: 'cid',
  'Domain Name': 'domain',
  'Email Address': 'email',
  'IP Address': 'ipAddress',
  'Phone Number': 'phoneNumber',
  PUID: 'puid',
  SkypeID: 'skypeID',
  'Tenant ID': 'tenantId',
  'Xbox Gamertag': 'xboxGamertag',
};

/** V2 data-category values (TypeOfServiceStep) to CMS DataCategory. */
const DATA_CATEGORY_MAP: Record<string, DataCategory> = {
  BasicSubscriberInformation: 'basicSubscriberInformation',
  AuthenticationLogs: 'authenticationLogs',
  TransactionalData: 'transactionalData',
  ServiceTelemetry: 'serviceTelemetryAndLogs',
  Content: 'content',
};

/**
 * Whether the wizard's identifier-type value has a CMS enum member to map onto.
 *
 * CMS binds identifierType to a closed enum and rejects unknown members outright
 * (LENS-CMS PatchTargetIdentifierRequestValidator IsInEnum). A value the wizard offers
 * but CMS has no member for cannot be submitted — the disposition of these legacy values
 * is the open cross-team decision tracked as G9. Submit is blocked rather than silently
 * dropping the identifier the user entered.
 */
export function isIdentifierTypeSupported(uiValue: string): boolean {
  return Object.prototype.hasOwnProperty.call(IDENTIFIER_TYPE_MAP, uiValue);
}

/**
 * Whether the wizard's data-category value has a CMS enum member to map onto.
 *
 * The same closed-enum rule (G9) applies to dataCategory. "Other" and its free-text
 * detail have no CMS home, so a request selecting them is blocked rather than filed with
 * the selection quietly discarded.
 */
export function isDataCategorySupported(uiValue: string): boolean {
  return Object.prototype.hasOwnProperty.call(DATA_CATEGORY_MAP, uiValue);
}

/** V2 nature-of-crime boolean keys to the strings CMS stores. */
const NATURE_OF_CRIME_LABELS: Record<string, string> = {
  isTerrorism: 'Terrorism',
  isHumanTrafficking: 'Human trafficking',
  isMissingPerson: 'Missing person',
  isCorruption: 'Corruption',
  isCybercrime: 'Cybercrime',
  isDefamation: 'Defamation',
  isHateSpeech: 'Hate speech',
  isMoneyLaundering: 'Money laundering',
  isHarassment: 'Harassment',
  isTheft: 'Theft',
  isViolentCrime: 'Violent crime',
  isAdultSexualAbuseOrExploitation: 'Adult sexual abuse or exploitation',
  isDrugsOrDrugTrafficking: 'Drugs or drug trafficking',
  isFraudWithMoneyLaundering: 'Fraud (with money laundering)',
  isFraudNoMoneyLaundering: 'Fraud (no money laundering)',
  isOther: 'Other',
};

/** The concern string a "Yes" to child exploitation carries onward to CMS. */
const CHILD_EXPLOITATION_CONCERN = 'Child exploitation related';

function trimmedOrUndefined(value: string | undefined | null): string | undefined {
  const text = (value ?? '').trim();
  return text === '' ? undefined : text;
}

function mapTargets(state: DomesticWizardState): CaseTarget[] {
  return state.typeOfService.identifiers
    .map((identifier): CaseTarget | null => {
      const identifierType = IDENTIFIER_TYPE_MAP[identifier.type];
      const value = identifier.value.trim();
      if (!identifierType || value === '') {
        return null;
      }
      return {
        identifierType,
        value,
        startDate: trimmedOrUndefined(identifier.startDate),
        endDate: trimmedOrUndefined(identifier.endDate),
      };
    })
    .filter((target): target is CaseTarget => target !== null);
}

function mapContacts(state: DomesticWizardState): CaseContact[] {
  const contacts: CaseContact[] = [];
  const agent = state.agentInfo;

  contacts.push({
    contactType: 'primarySubmitter',
    firstName: trimmedOrUndefined(agent.agentFirstName),
    lastName: trimmedOrUndefined(agent.agentLastName),
    email: trimmedOrUndefined(agent.agentEmail),
    phone: trimmedOrUndefined(agent.agentPhoneNumber),
  });

  if (agent.hasReceivingAgent && trimmedOrUndefined(agent.receivingAgentEmail)) {
    contacts.push({
      contactType: 'alternateReceiver',
      firstName: trimmedOrUndefined(agent.receivingAgentFirstName),
      lastName: trimmedOrUndefined(agent.receivingAgentLastName),
      email: trimmedOrUndefined(agent.receivingAgentEmail),
      phone: trimmedOrUndefined(agent.receivingAgentPhone),
    });
  }

  return contacts;
}

function mapNatureOfCrimes(state: DomesticWizardState): string[] {
  return Object.entries(NATURE_OF_CRIME_LABELS)
    .filter(([key]) => state.natureOfCrime[key as keyof typeof state.natureOfCrime] === true)
    .map(([, label]) => label);
}

/**
 * Builds the CMS payload from the wizard's in-memory state.
 *
 * Jurisdiction and priority are required by CMS but never asked on V2's domestic form,
 * so they take domestic defaults. Preservation is the gating Yes/No, so a preservation
 * request files under CMS's `preservation` type regardless of any instrument selection.
 */
export function buildDomesticCaseForm(state: DomesticWizardState): DomesticCaseForm {
  const isPreservation = state.typeOfRequest.isPreservationRequest === true;
  const isChildExploitation = state.typeOfRequest.cpConcern === true;

  const requestType: RequestType = isPreservation
    ? 'preservation'
    : REQUEST_TYPE_MAP[state.typeOfRequest.requestType ?? ''] ?? 'other';

  const subTypeLabel = state.typeOfRequest.requestSubType ?? '';
  const requestSubType =
    !isPreservation && SUB_TYPE_MAP[subTypeLabel] ? SUB_TYPE_MAP[subTypeLabel] : undefined;

  const dataCategories = (state.typeOfService.dataCategories ?? [])
    .map((category) => DATA_CATEGORY_MAP[category])
    .filter((category): category is DataCategory => Boolean(category));

  const services = [
    ...state.typeOfService.selectedServiceIds,
    ...state.typeOfService.manualServices,
  ].filter((service) => service.trim() !== '');

  const ndoApplicable = !isPreservation && !isChildExploitation;
  const hasNdo = state.ndo.hasNdo === true;

  const form: DomesticCaseForm = {
    requestType,
    jurisdiction: 'US',
    priority: 'standard',
    country: 'US',
    requestSubType,
    agencyName: trimmedOrUndefined(state.agentInfo.agencyName),
    leReferenceNumber: trimmedOrUndefined(state.typeOfRequest.leReferenceNumber),
    additionalCaseInformation: trimmedOrUndefined(state.natureOfCrime.isOtherDescription),
    natureOfCrimes: mapNatureOfCrimes(state),
    cpConcern: isChildExploitation ? CHILD_EXPLOITATION_CONCERN : undefined,
    isPreservationRequest: isPreservation,
    isWaShieldLaw: state.typeOfRequest.isWAShieldLaw ?? undefined,
    services,
    requestStartDate: trimmedOrUndefined(state.typeOfService.requestStartDate),
    requestEndDate: trimmedOrUndefined(state.typeOfService.requestEndDate),
    dataCategories,
    targets: mapTargets(state),
    contacts: mapContacts(state),
    nonDisclosure:
      ndoApplicable && hasNdo ? { userNotificationAllowed: false } : undefined,
  };

  return form;
}
